import axiosInstance from './AxiosInstance';
import toast from "react-hot-toast";
import {
    loginConfirmedAction,
    Logout,
} from '../store/actions/AuthActions';

const AuthService = {
    // 1. USER LOGIN (Updated to use identifier)
    login: async (identifier, password) => {
        try {
            let loginDomain = window.location.origin.split('//')[1]?.split('.')[0];
            loginDomain = loginDomain.includes('localhost') ? 'superadmin' : 'superadmin';
            const response = await axiosInstance.post(`/auth/${loginDomain}/login`, {
                identifier,
                password
            });
            return {
                success: true,
                data: {
                    user: response.data.data.user,
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken,
                },
                message: 'Login successful'
            };
        } catch (error) {
            // Log detailed error info for debugging
            try {
                console.error('AuthService.login error:', {
                    message: error.message,
                    code: error.code,
                    request: error.request,
                    response: error.response && {
                        status: error.response.status,
                        headers: error.response.headers,
                        data: error.response.data,
                    },
                });
            } catch (logErr) {
                console.error('Failed to log AuthService.login error', logErr);
            }

            // Handle requiresPasswordChange (403)
            if (error.response?.status === 403 && error.response?.data?.requiresPasswordChange) {
                return {
                    success: false,
                    requiresPasswordChange: true,
                    user: error.response.data.user,
                    message: error.response.data.message || 'Password change required'
                };
            }

            return {
                success: false,
                message: error.response?.data?.message || 'Login failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // NEW: Unified login using website/domain (external auth server)
    loginUsingDomain: async (website, identifier, password) => {
        try {
            const response = await axiosInstance.post('auth/login', {
                website,
                identifier,
                password
            });
            // Normalize response to the same shape used by other methods
            return {
                success: response.data.success,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            console.log('errorerrorerror', error)
            // Handle requiresPasswordChange (403)
            if (error.response?.status === 403 && error.response?.data?.requiresPasswordChange) {
                // Extract user data from response - it can be in data.data or directly in data
                const userData = error.response.data.data || {};
                return {
                    success: false,
                    requiresPasswordChange: true,
                    user: {
                        userId: userData.userId,
                        email: userData.email,
                        phoneNumber: userData.phoneNumber,
                        userCode: userData.userCode,
                        ...userData
                    },
                    message: error.response.data.message || 'Password change required'
                };
            }

            return {
                success: false,
                message: error.response?.data?.message || 'Login failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 2. USER REGISTRATION
    register: async (userData) => {
        try {
            const response = await axiosInstance.post('/auth/register', userData);
            
            return {
                success: response.data.success,
                data: {
                    user: response.data.data.user,
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken
                },
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Registration failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 3. REFRESH TOKEN
    refreshToken: async (refreshToken) => {
        try {
            const response = await axiosInstance.post('/auth/refresh-token', {
                refreshToken
            });
            
            return {
                success: response.data.success,
                data: {
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken
                },
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Token refresh failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 4. LOGOUT
    logout: async () => {
        try {
            const response = await axiosInstance.post('/auth/logout');
            
            return {
                success: response.data.success,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Logout failed'
            };
        }
    },

    // 5. FORGOT PASSWORD (Updated to use identifier and OTP)
    forgotPassword: async (identifier) => {
        try {
            const response = await axiosInstance.post('/auth/forgot-password', { identifier });
            
            return {
                success: true,
                message: response.data.message || 'If a matching account exists, a reset code has been sent.'
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to send reset code',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 6. RESET PASSWORD (Updated to use OTP)
    resetPassword: async (identifier, resetCode, newPassword) => {
        try {
            const response = await axiosInstance.post('/auth/reset-password', {
                identifier,
                resetCode,
                newPassword
            });
            
            return {
                success: true,
                message: response.data.message || 'Password reset successful. You may now log in.'
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Password reset failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 7. CHANGE PASSWORD FROM TEMPORARY (Public endpoint for password change requirement)
    changePasswordFromTemporary: async (identifier, temporaryPassword, newPassword, confirmPassword) => {
        try {
            const response = await axiosInstance.post('/auth/change-password', {
                currentPassword: temporaryPassword,
                identifier,
                temporaryPassword,
                newPassword,
                confirmPassword
            });
            
            return {
                success: true,
                data: response.data.data,
                message: response.data.message || 'Password changed successfully.'
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Password change failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 7b. SETUP PASSWORD (For authenticated users without password or mustChangePassword)
    setupPassword: async (newPassword) => {
        try {
            // Get temp user data if it exists (for password change requirement flow)
            const tempData = localStorage.getItem('tempUserData');
            let userId = null;
            
            if (tempData) {
                try {
                    const userData = JSON.parse(tempData);
                    userId = userData.user?.userId || userData.user?.id;
                } catch (err) {
                    console.error('Error parsing temp user data:', err);
                }
            }

            const payload = {
                newPassword
            };

            // Include userId if available from temp data
            if (userId) {
                payload.userId = userId;
            }

            const response = await axiosInstance.post('/auth/setup-password', payload);
            
            return {
                success: true,
                data: response.data.data,
                message: response.data.message || 'Password setup successful.'
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Password setup failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 7. CHANGE PASSWORD
    changePassword: async (currentPassword, newPassword, confirmPassword) => {
        try {
            const response = await axiosInstance.post('/auth/change-password', {
                currentPassword,
                newPassword,
                confirmPassword
            });
            
            return {
                success: response.data.success,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Password change failed',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 8. GET CURRENT USER
    getCurrentUser: async () => {
        try {
            const response = await axiosInstance.get('/auth/me');
            
            return {
                success: response.data.success,
                data: response.data.data.user,
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch user data',
                data: null
            };
        }
    },

    // 9. SWITCH COMPANY (SUPERADMIN only)
    switchCompany: async (companyId) => {
        try {
            const response = await axiosInstance.post('/auth/switch-company', {
                companyId
            });
            
            return {
                success: response.data.success,
                data: {
                    user: response.data.data.user,
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken
                },
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to switch company',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // 10. SWITCH BRANCH (ADMIN, STAFFS, CASHIER)
    switchBranch: async (branchId) => {
        try {
            const response = await axiosInstance.post('/auth/switch-branch', {
                branchId
            });
            console.log('response', response)
            return {
                success: response.data.success,
                data: {
                    user: response.data.data.user,
                    accessToken: response.data.data.accessToken,
                    refreshToken: response.data.data.refreshToken
                },
                message: response.data.message
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to switch branch',
                errors: error.response?.data?.errors || []
            };
        }
    },

    // Helper: Handle auth response (login, switch-company, switch-branch)
    handleAuthResponse: (response) => {
        if (response.success && response.data) {
            // Calculate expiration time (24 hours for access token)
            const expirationTime = new Date().getTime() + (24 * 60 * 60 * 1000);
            
            const tokenDetails = {
                ...response.data,
                expireDate: new Date(expirationTime),
                expiresIn: 24 * 60 * 60
            };
            
            // Save to localStorage
            localStorage.setItem('userDetails', JSON.stringify(tokenDetails));
            
            if (response.data.refreshToken) {
                localStorage.setItem('refreshToken', response.data.refreshToken);
            }
            
            return tokenDetails;
        }
        return null;
    }
};

// Legacy function exports for backward compatibility
export function signUp(email, password) {
    return AuthService.register({ email, password });
}

export function login(email, password) {
    return AuthService.login(email, password);
}

export function formatError(errorResponse) {
    const message = errorResponse?.message || errorResponse?.error?.message;
    
    // Prevent multiple toast popups by adding a delay
    setTimeout(() => {
        switch (message) {
            case 'EMAIL_EXISTS':
            case 'User already exists':
                toast.error("Email already exists");
                break;
            case 'EMAIL_NOT_FOUND':
            case 'User not found':
                toast.error("Email not found. Try again!");
                break;
            case 'INVALID_PASSWORD':
            case 'Invalid credentials':
            case 'Invalid password':
                toast.error("Invalid Password. Try again!");
                break;
            case 'USER_DISABLED':
            case 'Account disabled':
                toast.error("Account has been disabled");
                break;
            case 'INVALID_EMAIL':
            case 'Invalid email format':
                toast.error("Invalid email format");
                break;
            case 'TOKEN_EXPIRED':
            case 'Session expired':
                toast.error("Session Expired. Please login again");
                break;
            default:
                toast.error(message || "An error occurred");
                break;
        }
    }, 100); // Small delay to prevent conflicts with loading states
}

export function saveTokenInLocalStorage(tokenDetails) {
    // Calculate expiration time (24 hours for access token)
    const expirationTime = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
    
    const userDetails = {
        ...tokenDetails,
        expireDate: new Date(expirationTime),
        expiresIn: 24 * 60 * 60 // 24 hours in seconds
    };
    
    localStorage.setItem('userDetails', JSON.stringify(userDetails));
    
    // Store refresh token separately if provided
    if (tokenDetails.refreshToken) {
        localStorage.setItem('refreshToken', tokenDetails.refreshToken);
    }
}

export function getTokenFromLocalStorage() {
    const tokenDetailsString = localStorage.getItem('userDetails');
    if (tokenDetailsString) {
        return JSON.parse(tokenDetailsString);
    }
    return null;
}

export function getRefreshTokenFromLocalStorage() {
    return localStorage.getItem('refreshToken');
}

export function removeTokenFromLocalStorage() {
    localStorage.removeItem('userDetails');
    localStorage.removeItem('refreshToken');
    localStorage.clear()
}

export function runLogoutTimer(dispatch, timer, navigate) {
    setTimeout(() => {
        dispatch(Logout(navigate));
        removeTokenFromLocalStorage();
    }, timer);
}

export async function checkAutoLogin(dispatch, navigate) {
    const tokenDetails = getTokenFromLocalStorage();
    
    if (!tokenDetails) {
        dispatch(Logout(navigate));
        return;
    }

    let expireDate = new Date(tokenDetails.expireDate);
    let todaysDate = new Date();

    // Check if token is expired
    if (todaysDate > expireDate) {
        // Try to refresh token
        const refreshToken = getRefreshTokenFromLocalStorage();
        if (refreshToken) {
            try {
                const refreshResult = await AuthService.refreshToken(refreshToken);
                if (refreshResult.success) {
                    // Save new tokens
                    saveTokenInLocalStorage(refreshResult.data);
                    dispatch(loginConfirmedAction(refreshResult.data));
                    
                    // Set new timer
                    const timer = (24 * 60 * 60 * 1000); // 24 hours
                    runLogoutTimer(dispatch, timer, navigate);
                    return;
                }
            } catch (error) {
                console.error('Token refresh failed:', error);
            }
        }
        
        // If refresh failed or no refresh token, logout
        dispatch(Logout(navigate));
        removeTokenFromLocalStorage();
        return;
    }
    
    // Token is still valid
    dispatch(loginConfirmedAction(tokenDetails));
    
    const timer = expireDate.getTime() - todaysDate.getTime();
    runLogoutTimer(dispatch, timer, navigate);
}

export function isLogin() {
    const tokenDetails = getTokenFromLocalStorage();
    
    if (!tokenDetails) {
        return false;
    }
    
    // Check if token is expired
    const expireDate = new Date(tokenDetails.expireDate);
    const todaysDate = new Date();
    
    return todaysDate <= expireDate;
}

// Export the AuthService as default
export default AuthService;
